import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Skeleton, Space,
  Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { CreateQuoteRequest, QuoteRow } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Quote comparison.
 * Real quotes list under a requisition, "Select winner" flips is_selected;
 * lands on /admin/expenses/quotes/:code (:code is the requisition code).
 */
export function QuoteComparisonPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);

  const q = useQuery({
    queryKey: ['requisition', institution, code],
    queryFn: () => productApi.getRequisition(institution, code),
    enabled: !!institution && !!code,
  });
  const select = useMutation({
    mutationFn: (quoteId: string) => productApi.selectQuote(institution, code, quoteId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['requisition', institution, code] }); void message.success('Selected'); },
  });

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data) return <Alert type="error" showIcon message="Requisition not found" />;
  const { requisition: r, quotes, bills } = q.data;
  const cheapest = quotes.length > 0 ? Math.min(...quotes.map((x) => x.amount_paise)) : 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space size={4}>
        <Button type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/expenses/requisitions`)}>← Requisitions</Button>
        <Typography.Text type="secondary">/</Typography.Text>
        <Typography.Text style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{r.code}</Typography.Text>
      </Space>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>{r.subject}</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {r.category} · {r.requestor_name} · Estimate ₹{paise(r.amount_estimate_paise)} · Status <Tag>{r.status}</Tag>
          </Typography.Text>
        </div>
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add quote</Button>
        </Space>
      </div>

      <Card size="small" title={`Quotes (${quotes.length})`}>
        {quotes.length === 0 ? (
          <Empty description={<span>No quotes. <a onClick={() => setAddOpen(true)}>Add the first quote</a>.</span>} style={{ padding: 20 }} />
        ) : (
          <Table<QuoteRow>
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={quotes}
            columns={[
              { title: 'Vendor', dataIndex: 'vendor_name', key: 'v', render: (v: string, row) => <span><b>{v}</b> {row.amount_paise === cheapest && <Tag color="success" style={{ marginLeft: 6 }}>lowest</Tag>}</span> },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', width: 140, align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Note', dataIndex: 'note', key: 'n', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
              {
                title: 'Winner',
                key: 'w',
                width: 130,
                render: (_, row) => row.is_selected ? (
                  <Tag color="success">selected</Tag>
                ) : (
                  <Button size="small" onClick={() => select.mutate(row.id)}>Select</Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      {bills.length > 0 && (
        <Card size="small" title={`Bills raised (${bills.length})`}>
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={bills}
            columns={[
              { title: 'Vendor', dataIndex: 'vendor_name', key: 'v' },
              { title: 'Number', dataIndex: 'number', key: 'n', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Paid', dataIndex: 'paid_paise', key: 'p', align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Status', dataIndex: 'status', key: 's', render: (v: string) => <Tag color={v === 'paid' ? 'success' : v === 'partly_paid' ? 'warning' : 'default'}>{v.replace(/_/g, ' ')}</Tag> },
              { title: 'Due', dataIndex: 'due_on', key: 'd' },
            ]}
          />
        </Card>
      )}

      <AddQuoteModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        code={code}
        onCreated={() => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['requisition', institution, code] });
          void message.success('Quote added');
        }}
      />
    </Space>
  );
}

function AddQuoteModal({
  open, onClose, institution, code, onCreated,
}: { open: boolean; onClose: () => void; institution: string; code: string; onCreated: () => void }) {
  const [form] = Form.useForm<{ vendor_name: string; amount_rupees: number; note?: string }>();
  const m = useMutation({
    mutationFn: (v: CreateQuoteRequest) => productApi.addQuote(institution, code, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Add quote" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={(v) => m.mutate({
        vendor_name: v.vendor_name.trim(),
        amount_paise: Math.round(v.amount_rupees * 100),
        note: v.note?.trim() || undefined,
      })}>
        <Form.Item name="vendor_name" label="Vendor" rules={[{ required: true }]}>
          <Input placeholder="Reliable Supplies Pvt Ltd" autoFocus />
        </Form.Item>
        <Form.Item name="amount_rupees" label="Amount (₹)" rules={[{ required: true, type: 'number', min: 0 }]}>
          <InputNumber min={0} step={100} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="note" label="Note (delivery, warranty, brand)">
          <Input.TextArea rows={3} />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
